/**
 * La pagination — **une rangée, un compte, et des pastilles rondes**.
 *
 * Elle sert trois écrans : le journal d'audit, la recherche de transactions et
 * la fiche d'un acteur. Elle a d'abord vécu dans `AuditLogScreen`, écrite pour
 * lui, et elle en est sortie quand un second écran l'a demandée : la recopier
 * aurait fait deux paginations à tenir alignées, et le jour de la divergence
 * c'est l'un des deux écrans qui perdrait son nom accessible ou son
 * `aria-current` sans que personne le voie.
 *
 * ## Elle ne suit plus la maquette, et il faut le dire
 *
 * Le dessin met **trois** pastilles compactes à droite du compte, avec un `…`,
 * et **ni « précédente » ni « suivante »**. Ce commentaire l'a longtemps écrit,
 * et il ajoutait que le porteur avait refusé une barre de navigation au bas du
 * journal d'audit — « le dessin met quatre pastilles, les pastilles portent
 * tout ».
 *
 * **Le porteur a changé d'avis, après avoir vu mieux** : « j'aime beaucoup le
 * style de sa pagination, vaut mieux qu'on change le nôtre pour cela sur toutes
 * les pages ». La décision est la sienne, elle est postérieure au dessin, et
 * elle **inverse une consigne antérieure** — c'est exactement pourquoi elle est
 * écrite ici et inscrite au registre (`D-112`). Que personne ne la « corrige »
 * plus tard en revenant au dessin.
 *
 * Cinq écarts, et chacun se lit :
 *
 *  1. **cinq pastilles** au lieu de trois ;
 *  2. **première, précédente, suivante, dernière** — le dessin n'en a aucune ;
 *  3. **le compte passe EN LIGNE** avec les contrôles, au lieu d'être un bloc à
 *     leur gauche ;
 *  4. **le `…` disparaît** : il n'a plus de rôle une fois qu'on peut sauter à la
 *     première et à la dernière page d'un geste ;
 *  5. le sélecteur de lignes par page de la référence n'est **pas** repris —
 *     voir plus bas, c'est un refus motivé et non un oubli.
 *
 * ## Le sélecteur de lignes par page n'est pas là, et c'est délibéré
 *
 * `pageSize` vient du serveur : `TransactionsApi` le lit de
 * `backoffice.transactions.page_size`, une **borne de divulgation** posée en
 * configuration (V47) précisément pour qu'une consultation ne devienne pas une
 * extraction. Le rendre choisissable demande que la route *accepte* une borne,
 * qu'elle la **plafonne** par le paramètre configuré, et que la valeur retenue
 * entre dans l'entrée du journal d'audit. Tant que ces trois-là ne sont pas
 * écrits, une liste déroulante côté navigateur serait un contournement de borne
 * déguisé en confort.
 *
 * ## Ce que les liens de texte donnaient gratuitement, et qui est gardé
 *
 * Une pastille dont le texte visible est « 1 » n'annonce rien d'utile : un
 * lecteur d'écran lirait « 1, bouton ». Chacune porte donc un **nom
 * accessible** qui dit « Page 1 », la courante porte `aria-current="page"`, et
 * le groupe est une `<nav>` NOMMÉE. Les quatre flèches sont des **glyphes**, et
 * un glyphe n'est pas un nom : elles portent le leur, en toutes lettres.
 *
 * Aux extrémités, les flèches sont **désactivées** plutôt que masquées. Ce
 * n'est pas le « bouton qui ne fait rien » que ce produit refuse ailleurs : un
 * bouton inerte l'est *toujours*, et celui-ci redevient actif à la page
 * suivante. Le masquer ferait sauter la rangée d'un contrôle à chaque bord.
 */

import type { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

/**
 * Cinq rangs, comme la pagination que le porteur a choisie en montre cinq.
 *
 * Trois, la valeur d'avant, venait du dessin. Cinq est ce qu'il a demandé, et
 * ce que les quatre flèches rendent tenable : avec « première » et « dernière »,
 * une fenêtre plus large ne rallonge pas le chemin, elle le raccourcit.
 */
const LARGEUR = 5;

/** La première page. Nommée : c'est une borne, et elle sert à deux endroits. */
const PREMIERE = 1;

/** Ce que la pagination attend. */
export interface PaginationProps {
  /** Le bloc BEM de l'écran appelant — `audit-log`, `transactions`. */
  readonly block: string;
  /** Le préfixe des clés de traduction — `auditLog.pages`, `transactions.pages`. */
  readonly keyPrefix: string;
  /** La page courante, à partir de 1. */
  readonly page: number;
  /** Le nombre total de pages. Au moins une. */
  readonly pages: number;
  /** La borne d'une page, telle que le serveur l'a rendue. Elle nomme le groupe. */
  readonly pageSize: number;
  /** Ce que l'écran fait d'un rang choisi. La pagination ne charge rien elle-même. */
  readonly onGo: (page: number) => void;
  /**
   * Le **compte**, rendu dans la rangée plutôt qu'à côté d'elle.
   *
   * « Affichage 1 à 20 sur 730 transactions » est sur la même ligne que les
   * contrôles dans la pagination que le porteur a choisie. L'écran le fabrique —
   * lui seul sait ce qu'il compte —, la pagination lui donne sa place.
   */
  readonly summary?: ReactNode;
}

/** Les pastilles de page, au pied de la carte. */
export function Pagination({
  block,
  keyPrefix,
  page,
  pages,
  pageSize,
  onGo,
  summary,
}: PaginationProps): ReactNode {
  const { t } = useTranslation();
  const premier = Math.max(
    PREMIERE,
    Math.min(page - Math.floor(LARGEUR / 2), pages - LARGEUR + 1),
  );
  const rangs: number[] = [];
  for (let rang = premier; rang < premier + LARGEUR && rang <= pages; rang += 1) {
    rangs.push(rang);
  }

  /** Une flèche : un glyphe qui se voit, un nom qui s'annonce, une borne qui l'éteint. */
  const fleche = (nom: string, cible: number, eteinte: boolean): ReactNode => (
    <button
      className={`${block}__step`}
      type="button"
      disabled={eteinte}
      aria-label={t(`${keyPrefix}.${nom}`)}
      onClick={() => {
        onGo(cible);
      }}
    >
      <span aria-hidden="true">{t(`${keyPrefix}.${nom}Mark`)}</span>
    </button>
  );

  return (
    <nav className={`${block}__pages`} aria-label={t(`${keyPrefix}.landmark`, { pageSize })}>
      {summary === undefined ? null : (
        <span className={`${block}__pages-summary`}>{summary}</span>
      )}
      {fleche('first', PREMIERE, page <= PREMIERE)}
      {fleche('previous', Math.max(PREMIERE, page - 1), page <= PREMIERE)}
      {rangs.map((rang) => (
        <button
          className={`${block}__page`}
          key={rang}
          type="button"
          aria-current={rang === page ? 'page' : undefined}
          // Le texte VISIBLE est le rang ; le nom accessible dit « Page 3 ».
          // Sans lui, la pastille s'annoncerait « 3, bouton ».
          aria-label={t(`${keyPrefix}.goTo`, { page: rang })}
          onClick={() => {
            onGo(rang);
          }}
        >
          {rang}
        </button>
      ))}
      {fleche('next', Math.min(pages, page + 1), page >= pages)}
      {fleche('last', pages, page >= pages)}
    </nav>
  );
}
